"use client";

import React, { useCallback } from "react";
import { ResponsiveModal } from "@wtc/ui";
import { useGlobalModalStack } from "@wtc/ui";
import { useUpdateContact } from "@/features/contacts/hooks";
import { useToast } from "@/components/ui/toast";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Badge } from "@/components/ui/badge";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Loader2, Search, Link as LinkIcon, AlertCircle } from "lucide-react";
import { useLinkCompanySearch } from "@/features/contacts/hooks/contacts-link-company-search";

interface LinkCompanyModalProps {
  contactId: string;
  contactName: string;
  currentCompanyId?: string;
  currentCompanyName?: string;
}

export function LinkCompanyModal({
  contactId,
  contactName,
  currentCompanyId,
  currentCompanyName,
}: LinkCompanyModalProps) {
  const { isModalOpen, closeModal } = useGlobalModalStack();
  const { mutate: updateContact, isPending: isUpdating } = useUpdateContact();
  const { toast } = useToast();

  const {
    searchValue,
    setSearchValue,
    clearSearch,
    hasInput,
    hasSearchTerm,
    isDebouncing,
    displayedCompanies,
    isListLoading,
    ftsError,
    selectedCompanyId,
    setSelectedCompanyId,
    showReplaceConfirm,
    setShowReplaceConfirm,
    handleSelectCompany,
  } = useLinkCompanySearch({ contactId, currentCompanyId });

  const handleClose = useCallback(() => {
    closeModal("link-company");
    setSelectedCompanyId(null);
    clearSearch();
    setShowReplaceConfirm(false);
  }, [closeModal, setSelectedCompanyId, clearSearch, setShowReplaceConfirm]);

  const handleLinkCompany = useCallback(async () => {
    if (!selectedCompanyId) {
      toast({ type: "error", title: "Error", description: "Please select a company" });
      return;
    }

    const selectedCompany = displayedCompanies.find((c) => c.id === selectedCompanyId);
    if (!selectedCompany) return;

    try {
      await updateContact(
        {
          id: contactId,
          updates: { company_id: selectedCompanyId, company_name: selectedCompany.name },
        },
        {
          onSuccess: () => {
            const action = currentCompanyId ? "replaced" : "linked";
            toast({
              type: "success",
              title: "Success",
              description: `${contactName} has been ${action} to ${selectedCompany.name}`,
            });
            handleClose();
          },
          onError: () => {
            toast({
              type: "error",
              title: "Error",
              description: "Failed to link company. Please try again.",
            });
          },
        }
      );
    } catch {
      toast({
        type: "error",
        title: "Error",
        description: "Failed to link company. Please try again.",
      });
    }
  }, [
    selectedCompanyId,
    displayedCompanies,
    contactId,
    contactName,
    currentCompanyId,
    updateContact,
    toast,
    handleClose,
  ]);

  const isOpen = isModalOpen("link-company");

  return (
    <ResponsiveModal
      globalModalId="link-company"
      open={isOpen}
      onOpenChange={(open) => {
        if (!open) handleClose();
      }}
      title="Link Company"
      description={`Select a company to link to ${contactName}`}
      maxWidth="2xl"
    >
      <div className="space-y-4">
        {currentCompanyId && currentCompanyName && (
          <Alert>
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>
              This contact is currently linked to <strong>{currentCompanyName}</strong>. Selecting a
              new company will replace this link.
            </AlertDescription>
          </Alert>
        )}

        <div className="relative">
          <Search className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search by company name, website, or industry..."
            value={searchValue}
            onChange={(e) => setSearchValue(e.target.value)}
            className="pl-10"
            disabled={isUpdating}
          />
        </div>

        <div className="flex items-center justify-between text-xs text-muted-foreground">
          <span>
            {hasSearchTerm
              ? `FTS matches: ${displayedCompanies.length}`
              : "Browse mode: showing first 100 companies"}
          </span>
          {(isDebouncing || isListLoading) && (
            <span className="inline-flex items-center gap-1">
              <Loader2 className="h-3 w-3 animate-spin" />
              {isDebouncing ? "Typing..." : "Searching..."}
            </span>
          )}
        </div>

        {!hasSearchTerm && hasInput && (
          <Alert>
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>Type at least 2 characters to run full-text search.</AlertDescription>
          </Alert>
        )}

        {Boolean(ftsError) && hasSearchTerm && (
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>Search failed. Please try again.</AlertDescription>
          </Alert>
        )}

        <ScrollArea className="h-[400px] border rounded-lg p-4">
          {isListLoading ? (
            <div className="flex items-center justify-center h-full">
              <div className="text-center text-muted-foreground space-y-2">
                <Loader2 className="h-6 w-6 animate-spin mx-auto" />
                <p className="text-sm">
                  {hasSearchTerm ? "Searching companies..." : "Loading companies..."}
                </p>
              </div>
            </div>
          ) : displayedCompanies.length === 0 ? (
            <div className="flex items-center justify-center h-full text-muted-foreground">
              <p>{hasSearchTerm ? "No companies found" : "No available companies"}</p>
            </div>
          ) : (
            <div className="space-y-2">
              {displayedCompanies.map((company) => (
                <button type="button"
                  key={company.id}
                  onClick={() => handleSelectCompany(company)}
                  className={`w-full text-left p-3 rounded-lg border-2 transition-colors ${
                    selectedCompanyId === company.id
                      ? "border-blue-500 bg-blue-50 dark:bg-blue-950/40"
                      : "border-transparent hover:bg-gray-50 dark:hover:bg-gray-800"
                  }`}
                  disabled={isUpdating}
                >
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-sm truncate">{company.name}</p>
                      {company.website && (
                        <p className="text-xs text-muted-foreground truncate">{company.website}</p>
                      )}
                      {company.industry && (
                        <p className="text-xs text-muted-foreground">{company.industry}</p>
                      )}
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0">
                      {company.status && (
                        <Badge variant="outline" className="text-xs">
                          {company.status}
                        </Badge>
                      )}
                    </div>
                  </div>
                </button>
              ))}
            </div>
          )}
        </ScrollArea>

        {showReplaceConfirm && selectedCompanyId && (
          <Alert>
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>
              Click "Link Company" to replace the current link with{" "}
              <strong>{displayedCompanies.find((c) => c.id === selectedCompanyId)?.name}</strong>
            </AlertDescription>
          </Alert>
        )}

        <div className="flex justify-end gap-2 pt-4 border-t">
          <Button variant="outline" onClick={handleClose} disabled={isUpdating}>
            Cancel
          </Button>
          <Button
            onClick={handleLinkCompany}
            disabled={!selectedCompanyId || isUpdating}
            className="gap-2"
          >
            {isUpdating ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" />
                Linking...
              </>
            ) : (
              <>
                <LinkIcon className="h-4 w-4" />
                Link Company
              </>
            )}
          </Button>
        </div>
      </div>
    </ResponsiveModal>
  );
}
